/*
 * Keeps the cart footer honest when the cart stops needing shipping.
 *
 * The pickup-point app block (Parcely) builds its widget once, on page load,
 * and disables the checkout button until a location is picked. It never tears
 * that down, so removing the last physical item over AJAX leaves a shipping
 * picker — and a disabled checkout — on a digital-only cart. Shopify's section
 * rendering cannot help: the app block ships an empty <div> server-side and is
 * filled entirely by the app's own script.
 *
 * Patching the widget out of the DOM was tried and rejected: once its node is
 * gone the app cannot rebuild it, so a shopper who re-adds a physical product
 * would reach checkout with no pickup point at all. Reloading is the only fix
 * that does not depend on the app's internals, and it runs on one rare
 * transition — the last physical item leaving a cart that still has contents.
 */
class CartShippingSync extends HTMLElement {
  connectedCallback() {
    this.requiresShipping = this.dataset.requiresShipping === 'true';

    // Only worth doing when an app block actually owns part of the footer.
    this.hasAppBlock = !!document.querySelector('#main-cart-footer .shopify-app-block');
    if (!this.hasAppBlock) return;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    }

    // Coming back from checkout, iOS restores this page from the back/forward
    // cache with every script's state frozen as it was left — including a
    // checkout button the pickup-point app disabled. Nothing re-runs on a
    // restore, so the only reliable repair is to reload.
    this.onPageShow = (event) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', this.onPageShow);

    this.guardCheckoutButton();
  }

  // A cart that needs no shipping has nothing to choose a pickup point for, so
  // nothing may gate checkout on one. The app disables the button and never
  // re-enables it, which strands digital-only carts; this puts it back.
  // Deliberately narrow: it does nothing while shipping IS required, and
  // nothing on an empty cart, where Shopify disables the button on purpose.
  guardCheckoutButton() {
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }
    if (this.requiresShipping) return;

    const button = document.getElementById('checkout');
    if (!button || document.querySelectorAll('.cart-item').length === 0) return;

    const enable = () => {
      if (button.disabled) button.disabled = false;
      if (button.getAttribute('aria-disabled') === 'true') button.removeAttribute('aria-disabled');
    };
    enable();

    this.buttonObserver = new MutationObserver(enable);
    this.buttonObserver.observe(button, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    if (this.buttonObserver) this.buttonObserver.disconnect();
  }

  onCartUpdate(event) {
    const cart = event && event.cartData;

    // /cart/add.js returns only the added lines, so the flag has to be re-read.
    // Adding can never drop a shipping requirement, so this path never reloads
    // — but it must still run, or a later removal sees a stale "false".
    if (!cart || typeof cart.requires_shipping !== 'boolean') {
      fetch(`${routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((freshCart) => {
          this.requiresShipping = !!freshCart.requires_shipping;
          this.guardCheckoutButton();
        })
        .catch((error) => console.error(error));
      return;
    }

    const wasRequired = this.requiresShipping;
    this.requiresShipping = cart.requires_shipping;
    this.guardCheckoutButton();

    if (wasRequired && !cart.requires_shipping) {
      // Let the other cartUpdate subscribers finish first — the upsell box
      // records what was just removed in this same tick.
      setTimeout(() => window.location.reload(), 0);
    }
  }
}

customElements.define('cart-shipping-sync', CartShippingSync);
